import { Lock } from "@material-ui/icons";
import { Card, IconButton, ThemeProvider, Tooltip } from "@mui/material";
import LoadingScreen from "components/loading/LoadingScreen";
import Table from "components/table/Table";
import { ALL_OPTION } from "constant";
import { LIST_USER_LEVEL, LIST_USER_ROLE } from "constant/user";
import { useModalState } from "hooks/useModalState";
import { usePagination } from "hooks/usePagination";
import { useCallback, useMemo, useState } from "react";
import {
  useCreateUser,
  useEditUser,
  useExportListUser,
  useGetCountListUser,
  useGetListRole,
  useGetListUser,
} from "service/query/user.query";
import muiTheme from "theme/muiTheme";

import DialogBlockUser from "./components/DialogBlockUser";
import DialogUpdateUser from "./components/DialogUpdateUser";
import UserFilter from "./components/UserFilter";

const DEFAULT_VALUES = {
  username: "",
  fullname: "",
  phoneNumber: "",
  status: ALL_OPTION.value,
  role: ALL_OPTION.value,
  level: ALL_OPTION.value,
  city: ALL_OPTION.value,
  district: ALL_OPTION.value,
  postoffice: ALL_OPTION.value,
};

const convertFormValueToWhere = ({
  username,
  fullname,
  phoneNumber,
  status,
  role,
  level,
  city,
  district,
  postoffice,
}) => ({
  ...(username && {
    username: { like: username },
  }),
  ...(fullname && {
    fullname: { like: fullname },
  }),
  ...(phoneNumber && {
    phoneNumber: { like: phoneNumber },
  }),
  ...(status !== ALL_OPTION.value && {
    status,
  }),
  ...(role !== ALL_OPTION.value && {
    role,
  }),
  ...(level !== ALL_OPTION.value && {
    level,
  }),
  ...(city !== ALL_OPTION.value && {
    city,
  }),
  ...(district !== ALL_OPTION.value && {
    district,
  }),
  ...(postoffice !== ALL_OPTION.value && {
    postoffice,
  }),
});

const User = () => {
  const [where, setWhere] = useState(convertFormValueToWhere(DEFAULT_VALUES));
  const [currentUser, setCurrentUser] = useState(null);
  const blockUserModal = useModalState();
  const exportListUserQuery = useExportListUser({ filter: { where } });
  const updateUserModal = useModalState();
  const createUserQuery = useCreateUser();
  const editUserQuery = useEditUser();
  const { data: listRole } = useGetListRole();

  const {
    page,
    pageSize,
    limit,
    skip,
    handleChangePage,
    handleChangePageSize,
  } = usePagination();

  const handleSubmit = (values) => {
    setWhere(convertFormValueToWhere(values));
  };

  const { data: listUser, isFetching } = useGetListUser({
    filter: {
      order: "createdAt DESC",
      where,
      limit,
      skip,
    },
  });

  const { data: count, isFetching: isFetchingCount } = useGetCountListUser({
    where,
  });

  const handleOpenModalBlockUser = useCallback(
    (record) => {
      setCurrentUser(record);
      blockUserModal.openModal();
    },
    [blockUserModal]
  );

  const handleClickDownload = () => {
    exportListUserQuery.refetch();
  };

  const columns = useMemo(
    () => [
      {
        id: "username",
        name: "Tài khoản",
      },
      { id: "fullname", name: "Họ tên" },
      {
        id: "phoneNumber",
        name: "Số điện thoại",
      },
      { id: "email", name: "Email" },
      {
        id: "role",
        name: "Quyền",
        render: (record) =>
          record.role
            ? LIST_USER_ROLE.find((item) => item.roleName === record.role)
                ?.roleDisplayName
            : "",
      },
      {
        id: "level",
        name: "Cấp quản lý",
        render: (record) =>
          record.level
            ? LIST_USER_LEVEL.find((item) => item.value === record.level)?.label
            : "",
      },
      {
        id: "city_name",
        name: "BĐT/TP",
        render: (record) => (record.level ? record.city_name || "Tất cả" : ""),
      },
      {
        id: "district_name",
        name: "BĐH",
        render: (record) =>
          record.level ? record.district_name || "Tất cả" : "",
      },
      {
        id: "postoffice_name",
        name: "Bưu cục",
        render: (record) =>
          record.level ? record.postoffice_name || "Tất cả" : "",
      },
      {
        id: "status",
        name: "Trạng thái hoạt động",
        render: (record) => {
          return record?.status ? "Đang hoạt động" : "Ngưng hoạt động";
        },
      },
      {
        id: "chatStatus",
        name: "Trạng thái chat",
        render: (record) => {
          return record?.isActiveChat ? "Đang hoạt động" : "Ngưng hoạt động";
        },
      },
      {
        id: "createdAt",
        name: "Thời gian tạo",
        type: "datetime",
      },
    ],
    []
  );

  const handleOpenCreateUser = () => {
    setCurrentUser(null);
    updateUserModal.openModal();
  };

  const handleOpenEditUser = (record) => {
    setCurrentUser(record);
    updateUserModal.openModal();
  };

  const handleCloseUpdateUserModal = () => {
    updateUserModal.closeModal();
    setCurrentUser(null);
  };

  const handleSubmitUpdateUser = (formValues) => {
    const { id, ...payload } = formValues;
    if (id) {
      editUserQuery.mutate(formValues, {
        onSuccess: handleCloseUpdateUserModal,
      });
    } else {
      createUserQuery.mutate(payload, {
        onSuccess: handleCloseUpdateUserModal,
      });
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Card sx={{ p: 2 }}>
        <UserFilter
          onSubmit={handleSubmit}
          defaultValues={DEFAULT_VALUES}
          onClickDownload={handleClickDownload}
        />
      </Card>

      <Card sx={{ mt: 2 }}>
        <Table
          toolbar={{
            title: "Danh sách tài khoản",
            onCreate: handleOpenCreateUser,
          }}
          columns={columns}
          data={listUser}
          actionColumn={{
            onEdit: handleOpenEditUser,
            customActions: (record) =>
              record?.status ? (
                <Tooltip title="Khóa tài khoản">
                  <IconButton
                    onClick={() => handleOpenModalBlockUser(record)}
                    color="inherit">
                    <Lock />
                  </IconButton>
                </Tooltip>
              ) : null,
          }}
          pagination={{
            page,
            pageSize,
            totalItems: count,
            onPageChange: handleChangePage,
            onPageSizeChange: handleChangePageSize,
          }}
        />
      </Card>

      <LoadingScreen
        loading={
          isFetching || isFetchingCount || exportListUserQuery.isFetching
        }
      />

      {updateUserModal.open && (
        <DialogUpdateUser
          onSubmit={handleSubmitUpdateUser}
          item={currentUser}
          open={updateUserModal.open}
          onClose={handleCloseUpdateUserModal}
          loadingSaveUser={createUserQuery.isPending || editUserQuery.isPending}
          listRole={listRole}
        />
      )}

      {currentUser && (
        <DialogBlockUser
          open={blockUserModal.open}
          onClose={() => {
            blockUserModal.closeModal();
            setCurrentUser(null);
          }}
          user={currentUser}
        />
      )}
    </ThemeProvider>
  );
};

export default User;
